import React, {useCallback, useState} from 'react';
import {Box, Button, Divider, Typography} from '@mui/material';
import SellItemCard from './SellItemCard';
import {SelectedGroupedItem} from './types/Inventory';
import api from '../../../api/axios';
import {toast} from 'react-toastify';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store';
import {formatMoney, moneyToMinor} from '../../utils/UnitConversion.utils';
import ExtensionStatus from '../../extension/ExtensionStatus';

type Props = {
  items: SelectedGroupedItem[];
  steamAccountId: string;
  onRemoveItem: (key: string) => void;
  onClear: () => void;
  onQuantityChange: (key: string, quantity: number) => void;
  onPriceChange: (key: string, price: number) => void;
  onCloseMobileCart?: () => void;
  onSuccess: (soldAssetIds: string[]) => void;
};

const ToBeSoldItems: React.FC<Props> = ({
                                          items,
                                          steamAccountId,
                                          onRemoveItem,
                                          onClear,
                                          onQuantityChange,
                                          onPriceChange,
                                          onCloseMobileCart,
                                          onSuccess,
                                        }) => {
  const [isSelling, setIsSelling] = useState(false);
  const currencyRate = useSelector((state: RootState) => state.currency.rate);
  const currencySymbol = useSelector((state: RootState) => state.currency.symbol);

  const handleQtyChange = useCallback(
      (key: string, qty: number) => {
        onQuantityChange(key, qty);
      },
      [onQuantityChange]
  );

  const handlePriceChange = useCallback(
      (key: string, price: number) => {
        onPriceChange(key, price);
      },
      [onPriceChange]
  );

  const handleRemove = useCallback(
      (key: string) => {
        onRemoveItem(key);
      },
      [onRemoveItem]
  );

  const totalCount = items.reduce((sum, i) => sum + i.quantity, 0);

  // Calculate pricing based on the current user currency rate
  const subTotal = items.reduce(
      (sum, item) => {
        const itemPriceInCurrency = item.price * currencyRate;
        const finalItemPrice = itemPriceInCurrency < 0.02 ? 0.02 : itemPriceInCurrency;
        return sum + finalItemPrice * item.quantity;
      },
      0
  );
  const fee = subTotal * 0.01875;
  const totalEarnings = subTotal - fee;

  const handleSell = async () => {
    if (items.length === 0) return;

    const soldAssetIds: string[] = [];

    const sellItems = items.flatMap((group) => {
      const selected = group.items.slice(0, group.quantity);
      const basePrice = group.price < 0.02 ? 0.02 : group.price;

      return selected.map((it) => {
        soldAssetIds.push(it.itemAssetId);
        return {
          itemAssetId: it.itemAssetId,
          priceMinor: moneyToMinor(basePrice),
          isPublic: true,
        };
      });
    });

    try {
      setIsSelling(true);

      await toast.promise(
          api.post(
              `/steam-account/${steamAccountId}/inventory/sell`,
              sellItems
          ),
          {
            pending: 'Verkaufsangebote werden erstellt...',
            success: 'Angebote erfolgreich erstellt!',
            error: 'Erstellen der Verkaufsangebote fehlgeschlagen.',
          }
      );

      onClear();
      onCloseMobileCart?.();
      onSuccess(soldAssetIds);
    } finally {
      setIsSelling(false);
    }
  };

  return (
      <Box
          className="relative bg-neutral-900/70 rounded-xl p-3 flex flex-col md:w-96 w-full overflow-y-auto md:h-[87vh] h-[89vh] md:max-h-none md:overflow-y-auto">

        {onCloseMobileCart && (
            <button
                className="md:hidden absolute right-4 top-4 text-white text-2xl z-10"
                onClick={onCloseMobileCart}
            >
              ✕
            </button>
        )}

        {/* Extension Status & Header Section */}
        <div className="flex flex-col space-y-4 mb-4">
          <ExtensionStatus variant="simple" className="hidden lg:block" />
          <div className="flex justify-between items-end">
            <Typography variant="h6" className="text-white font-semibold leading-none">
              {totalCount} {totalCount === 1 ? 'item' : 'items'} for sale
            </Typography>
            {items.length > 0 && (
                <button
                    onClick={onClear}
                    className="text-[10px] font-black uppercase tracking-wider text-red-500 hover:text-red-400 transition-colors"
                >
                  Leeren
                </button>
            )}
          </div>
        </div>

        <Divider className="border-white/5 mb-2"/>

        <div className="flex-1 overflow-y-auto pr-1 space-y-2 mt-2 scrollbar-thin scrollbar-thumb-gray-700">
          {items.length === 0 && (
              <Typography className="text-gray-500 text-center py-10 italic">
                Noch keine Items ausgewählt.
              </Typography>
          )}

          {items.map((item) => (
              <SellItemCard
                  key={item.key}
                  item={item}
                  onRemove={handleRemove}
                  onQuantityChange={handleQtyChange}
                  onPriceChange={handlePriceChange}
              />
          ))}
        </div>

        {/* Pricing Summary */}
        <div className="flex flex-col text-sm text-gray-300 mt-4 space-y-1">
          <div className="flex justify-between">
            <span className="text-gray-500 uppercase text-[10px] font-black">Subtotal</span>
            <div className="flex font-mono font-bold">
              <span className="mr-1 text-gray-500">{currencySymbol}</span>
              <span>{formatMoney(subTotal)}</span>
            </div>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-500 uppercase text-[10px] font-black font-mono">Sale Fee (1.875%)</span>
            <div className="flex font-mono font-bold text-red-400/80">
              <span className="mr-1 opacity-60">{currencySymbol}</span>
              <span>-{formatMoney(fee)}</span>
            </div>
          </div>
        </div>

        <Divider className="border-white/5 my-3"/>

        <div className="flex justify-between items-center mb-4">
          <span className="text-white uppercase text-[11px] font-black tracking-widest">Total Earnings</span>
          <div className="flex text-xl font-black text-white font-mono">
            <span className="mr-1 text-blue-500">{currencySymbol}</span>
            <span>{formatMoney(totalEarnings)}</span>
          </div>
        </div>

        <Button
            fullWidth
            variant="contained"
            disabled={items.length === 0 || isSelling}
            onClick={handleSell}
            className={`!py-3 !rounded-xl !text-xs !font-black !uppercase !tracking-[0.1em] !shadow-2xl transition-all ${
                items.length > 0 && !isSelling
                    ? '!bg-blue-600 hover:!bg-blue-500 !text-white'
                    : '!bg-neutral-800 !text-gray-500'
            }`}
        >
          {isSelling ? 'Wird verkauft...' : 'Verkaufen'}
        </Button>
      </Box>
  );
};

export default ToBeSoldItems;